// ============================================================
// InventoryTab.jsx - internal house inventory, never shown to
// guests. Structured: item name, quantity, condition, photo, notes.
// ============================================================

import { useState, useEffect, useCallback } from 'react';

const IconPlus = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 5v14M5 12h14"/></svg>;
const IconTrash = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>;
const IconBox = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 8L12 3 3 8v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg>;

const CONDITION_LABELS = { good: 'Bon etat', worn: 'Use', damaged: 'Endommage', missing: 'Manquant' };
const CONDITION_COLORS = { good: 'bg', worn: 'ba', damaged: 'br', missing: 'bgr' };

function ItemRow({ item, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);

  return (
    <tr>
      <td>
        {editing ? (
          <input className="fi" defaultValue={item.item_name} onBlur={(e) => onUpdate({ item_name: e.target.value })} style={{ padding: '5px 8px', fontSize: 13 }} />
        ) : (
          <span style={{ fontWeight: 600 }} onClick={() => setEditing(true)}>{item.item_name}</span>
        )}
      </td>
      <td>
        <input className="fi" type="number" min="0" defaultValue={item.quantity} onBlur={(e) => onUpdate({ quantity: parseInt(e.target.value) || 0 })} style={{ width: 60, padding: '5px 8px', fontSize: 13 }} />
      </td>
      <td>
        <select className="fsel2" defaultValue={item.condition} onChange={(e) => onUpdate({ condition: e.target.value })} style={{ padding: '5px 8px', fontSize: 12 }}>
          {Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </td>
      <td style={{ fontSize: 12, color: 'var(--g2)' }}>
        <input className="fi" defaultValue={item.notes || ''} onBlur={(e) => onUpdate({ notes: e.target.value })} placeholder="Notes..." style={{ padding: '5px 8px', fontSize: 12 }} />
      </td>
      <td>
        <button className="btn btn-d btn-sm btn-icon" onClick={onDelete} type="button" title="Supprimer">{IconTrash}</button>
      </td>
    </tr>
  );
}

export default function InventoryTab({ propertyId, m, toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItemName, setNewItemName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/inventory`).then(r => r.json())
      .then(d => { if (d.success) setItems(d.data); })
      .finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    if (!newItemName.trim()) return;
    const res = await m(`/admin/properties/${propertyId}/inventory`, {
      method: 'POST',
      body: JSON.stringify({ item_name: newItemName.trim(), quantity: 1, condition: 'good' }),
    });
    const data = await res.json();
    if (data.success) { setNewItemName(''); toast('Objet ajoute', 'ok'); load(); }
    else toast(data.error || 'Erreur', 'err');
  };

  const updateItem = async (itemId, patch) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...patch } : i));
    await m(`/admin/inventory/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Supprimer cet objet de l\'inventaire ?')) return;
    const res = await m(`/admin/inventory/${itemId}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.success) { toast('Supprime', 'ok'); load(); }
  };

  const conditionCounts = items.reduce((acc, i) => {
    acc[i.condition] = (acc[i.condition] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--g2)', marginBottom: 14, background: 'var(--bg-alt)', padding: '10px 12px', borderRadius: 10 }}>
        Inventaire interne uniquement — jamais affiche dans le guide invite. Utilise pour le suivi materiel entre les sejours.
      </div>

      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {Object.entries(conditionCounts).map(([cond, count]) => (
            <span key={cond} className={`badge ${CONDITION_COLORS[cond]}`}>{count} {CONDITION_LABELS[cond]}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          className="fi"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          placeholder="Nom de l'objet (ex: Canape 3 places)"
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
        />
        <button className="btn btn-p" onClick={addItem} type="button">{IconPlus} Ajouter</button>
      </div>

      {loading ? <div className="empty"><span className="spin" /></div> : items.length === 0 ? (
        <div className="empty">
          <div className="empty-ico">{IconBox}</div>
          <div className="empty-t">Aucun objet dans l'inventaire</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Objet</th>
                <th>Qte</th>
                <th>Etat</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <ItemRow key={item.id} item={item} onUpdate={(patch) => updateItem(item.id, patch)} onDelete={() => deleteItem(item.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
